import React, { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import {
  FaFacebookF,
  FaLinkedinIn,
  FaTelegramPlane,
  FaTwitter,
  FaWhatsapp,
} from "react-icons/fa";
import type { IconType } from "react-icons";

import Header from "../components/Header";
import Leftnav from "../components/Leftnav";
import Rightchat from "../components/Rightchat";
import Popupchat from "../components/Popupchat";
import Appfooter from "../components/Appfooter";

import { getClubProfileBySlug } from "../redux/slices/clubSlice";
import { fetchClientPosts } from "../redux/slices/postSlice";

import {
  fetchClubAboutSection,
  fetchClubMembersSection,
  fetchClubEventsSection,
  fetchClubDocumentsSection,
  fetchClubMediaSection,
} from "../redux/slices/clubSectionSlice";

import { RootState } from "../redux/store";
import VerifiedTick from "../components/VerifiedTick";

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://api.bsangam.com"
)
  .replace(/\/uploads\/?$/, "")
  .replace(/\/$/, "");

// utils/image.ts

export const getImageUrl = (file?: string | null) => {
  if (!file || String(file).trim() === "") {
    return "/assets/images/user.png";
  }

  const value = String(file).trim();

  // Full URL already
  if (
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return encodeURI(value);
  }

  // /uploads/file.jpg
  if (value.startsWith("/uploads/")) {
    return encodeURI(`${API_URL}${value}`);
  }

  // uploads/file.jpg
  if (value.startsWith("uploads/")) {
    return encodeURI(`${API_URL}/${value}`);
  }

  // Files stored directly under public folders
  if (
    value.startsWith("designs/") ||
    value.startsWith("documents/") ||
    value.startsWith("banners/") ||
    value.startsWith("logos/")
  ) {
    return encodeURI(`${API_URL}/${value}`);
  }

  // Absolute relative path
  if (value.startsWith("/")) {
    return encodeURI(`${API_URL}${value}`);
  }

  // Default upload folder
  return encodeURI(`${API_URL}/uploads/${value}`);
};

const getFileUrl = (file?: string | null) => {
  if (!file || String(file).trim() === "") {
    return "#";
  }

  const value = String(file).trim();

  // Full URL already
  if (
    value.startsWith("http://") ||
    value.startsWith("https://")
  ) {
    return encodeURI(value);
  }

  // /uploads/file
  if (value.startsWith("/uploads/")) {
    return encodeURI(`${API_URL}${value}`);
  }

  // uploads/file
  if (value.startsWith("uploads/")) {
    return encodeURI(`${API_URL}/${value}`);
  }

  // /documents/file etc.
  if (value.startsWith("/")) {
    return encodeURI(`${API_URL}${value}`);
  }

  return encodeURI(`${API_URL}/${value}`);
};

const formatDate = (date?: string | null) => {
  if (!date) return "-";

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;

  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (date?: string | null) => {
  if (!date) return "-";

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getDateBox = (date?: string | null) => {
  if (!date) {
    return {
      day: "--",
      month: "---",
      year: "",
    };
  }

  const d = new Date(date);
  if (Number.isNaN(d.getTime())) {
    return {
      day: "--",
      month: "---",
      year: "",
    };
  }

  return {
    day: d.toLocaleDateString("en-IN", { day: "2-digit" }),
    month: d.toLocaleDateString("en-IN", { month: "short" }).toUpperCase(),
    year: d.toLocaleDateString("en-IN", { year: "numeric" }),
  };
};

const getAge = (dob?: string | null) => {
  if (!dob) return "-";

  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return "-";

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age -= 1;
  }

  return age > 0 ? age : "-";
};

const getFileExtension = (fileName?: string | null) => {
  if (!fileName) return "FILE";
  const cleanName = fileName.split("?")[0];
  const parts = cleanName.split(".");
  return parts.length > 1 ? parts.pop()?.toUpperCase() || "FILE" : "FILE";
};

const getFileStyle = (fileType?: string | null) => {
  const type = (fileType || "").toLowerCase();

  if (type.includes("pdf")) {
    return { icon: "PDF", bg: "#fff1f0", color: "#ff4d4f" };
  }

  if (type.includes("doc")) {
    return { icon: "DOCX", bg: "#eef5ff", color: "#2f80ed" };
  }

  if (type.includes("xls")) {
    return { icon: "XLSX", bg: "#eefbf3", color: "#23a455" };
  }

  if (type.includes("ppt")) {
    return { icon: "PPTX", bg: "#eef4ff", color: "#2F6FE4" };
  }

  return { icon: type ? type.toUpperCase() : "FILE", bg: "#f5f5f5", color: "#777" };
};

const sectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  marginBottom: "14px",
};

const softCardStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #eef1f5",
  borderRadius: "18px",
  boxShadow: "0 8px 24px rgba(15,23,42,0.05)",
};


const cleanHtml = (html?: string | null) => {
  if (!html) return "";
  return html.replace(/<img[^>]*src=["']\/\/:0["'][^>]*>/g, "");
};

const InfoBox = ({ label, value }: { label: string; value: any }) => (
  <div
    className="text-center rounded-xxl"
    style={{
      background: "#f7f8fb",
      padding: "10px 8px",
      minHeight: "56px",
      flex: 1,
    }}
  >
    <div className="font-xssss text-grey-600 mb-1">{label}</div>
    <div className="fw-600 text-grey-900 font-xssss">{value || "-"}</div>
  </div>
);

const MediaPlaceholder = ({ compact = false }: { compact?: boolean }) => (
  <div
    className={`club-media-placeholder${compact ? " compact" : ""}`}
    aria-label="Image not available"
  >
    <i className="feather-image" />
  </div>
);

const PrivateMessage = ({ text }: { text?: string }) => (
  <div className="text-center py-4">
    <div style={{ fontSize: "28px", marginBottom: "8px" }}>🔒</div>
    <p className="text-grey-600 font-xssss mb-0">
      {text || "This section is private."}
    </p>
  </div>
);

const ClubPostCard = ({ post }: { post: any }) => {
  return (
    <div
      className="mb-2"
      style={{
        background: "transparent",
        borderRadius: "18px",
        padding: "12px 6px",
      }}
    >
      <div className="d-flex align-items-center mb-2">
        <img
          src={getImageUrl(post.logo_image)}
          alt={post.client_name}
          style={{
            width: "42px",
            height: "42px",
            borderRadius: "50%",
            objectFit: "cover",
            background: "#f1f3f6",
            border: "2px solid #f5f5f5",
          }}
        />

        <div className="ms-3 flex-grow-1">
          <h4 className="fw-600 text-grey-900 font-xssss mb-0">
            {post.client_name || "Club"}
          </h4>

          <p className="font-xssss text-grey-500 mb-0">
            {post.client_user_name || "Admin"} · {formatDate(post.created_at)}

            {post.visibility === "private" && (
              <span
                className="ms-2 badge rounded-pill"
                style={{
                  background: "#fff3cd",
                  color: "#856404",
                  fontSize: "10px",
                  padding: "4px 8px",
                }}
              >
                Private
              </span>
            )}
          </p>
        </div>

        <button
          className="btn-round-sm border-0"
          style={{ background: "#eef2f7" }}
        >
          <span className="feather-more-horizontal font-xss text-grey-700" />
        </button>
      </div>

      {(post.title || post.short_description) && (
        <h4
          className="fw-600 text-grey-900 mb-2"
          style={{
            fontSize: "20px",
            lineHeight: "25px",
          }}
        >
          {post.title || post.short_description}
        </h4>
      )}

      {post.short_description && post.title && (
        <p
          className="font-xssss text-grey-700 mb-2"
          style={{ lineHeight: "22px" }}
        >
          {post.short_description}
        </p>
      )}

      {post.post_content && (
        <div
          className="font-xssss text-grey-700 mb-2"
          style={{
            lineHeight: "22px",
            maxHeight: "88px",
            overflow: "hidden",
          }}
          dangerouslySetInnerHTML={{
            __html: cleanHtml(post.post_content),
          }}
        />
      )}

      {post.post_image && (
        <img
          src={getImageUrl(post.post_image)}
          alt={post.title || "post"}
          style={{
            width: "100%",
            height: "340px",
            objectFit: "cover",
            borderRadius: "10px",
            marginTop: "10px",
          }}
        />
      )}

      <div
        className="d-flex align-items-center gap-4 mt-3 pt-2"
        style={{ borderTop: "1px solid rgba(0,0,0,0.05)" }}
      >
        <div className="font-xssss text-grey-700">❤️ {post.like_count || 0}</div>
        <div className="font-xssss text-grey-700">💬 {post.comment_count || 0}</div>
        <div className="font-xssss text-grey-700">↗️ {post.share_count || 0}</div>
        <div className="font-xssss text-grey-700">👁 {post.view_count || 0}</div>
      </div>
    </div>
  );
};

const SocialIcon = ({
  icon,
}: {
  icon: IconType;
}) => {
  return React.createElement(
    icon as React.ElementType
  );
};

const ClubHomePage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { slug } = useParams();

  const [activeTab, setActiveTab] = useState("posts");
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareRef = useRef<HTMLDivElement | null>(null);

  const { clubProfile, profileLoading, error } = useSelector(
    (state: RootState) => state.club
  );

  const { clientPosts: clubPosts, loading: postsLoading } = useSelector(
    (state: RootState) => state.posts
  );

  const {
    aboutData,
    membersData,
    eventsData,
    documentsData,
    mediaData,
    aboutLoading,
    membersLoading,
    eventsLoading,
    documentsLoading,
    mediaLoading,
  } = useSelector((state: RootState) => state.clubSection);

  const loggedUser = JSON.parse(localStorage.getItem("user") || "null");

  useEffect(() => {
    if (slug) {
      dispatch(getClubProfileBySlug(slug));
    }
  }, [dispatch, slug]);

  useEffect(() => {
    if (clubProfile?.client_id) {
      dispatch(
        fetchClientPosts({
          client_id: Number(clubProfile.client_id),
          user_id: loggedUser?.user_id
            ? Number(loggedUser.user_id)
            : undefined,
        })
      );
    }
  }, [dispatch, clubProfile?.client_id, loggedUser?.user_id]);

  useEffect(() => {
    if (!clubProfile?.client_id) return;

    const client_id = Number(clubProfile.client_id);
    const user_id = loggedUser?.user_id
      ? Number(loggedUser.user_id)
      : undefined;

    if (activeTab === "about") {
      dispatch(fetchClubAboutSection({ client_id, user_id }));
    }

    if (activeTab === "members") {
      dispatch(fetchClubMembersSection({ client_id, user_id }));
    }

    if (activeTab === "events") {
      dispatch(fetchClubEventsSection({ client_id, user_id }));
    }

    if (activeTab === "documents") {
      dispatch(fetchClubDocumentsSection({ client_id, user_id }));
    }

    if (activeTab === "media") {
      dispatch(fetchClubMediaSection({ client_id, user_id }));
    }
  }, [dispatch, activeTab, clubProfile?.client_id, loggedUser?.user_id]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        shareRef.current &&
        !shareRef.current.contains(event.target as Node)
      ) {
        setShareOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const clubSlug =
    clubProfile?.unique_slug ||
    slug ||
    "";

  const clubName =
    clubProfile?.club_name ||
    clubProfile?.client_name ||
    clubProfile?.full_name ||
    "Bharat Sangam Club";

  const shareUrl = clubSlug
    ? `${API_URL}/share/club/${encodeURIComponent(clubSlug)}`
    : "";

const handleClubShare = (type: string) => {
  if (!clubSlug || !shareUrl) {
    console.error("Club share URL is missing");
    return;
  }

  const shareText =
    `Check out ${clubName} on Bharat Sangam`;

  const message =
    `${shareText}\n${shareUrl}`;

  const encodedUrl =
    encodeURIComponent(shareUrl);

  const encodedText =
    encodeURIComponent(shareText);

  let selectedUrl = "";

  switch (type) {
    case "whatsapp":
      selectedUrl =
        `https://wa.me/?text=${encodeURIComponent(message)}`;
      break;

    case "facebook":
      selectedUrl =
        `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
      break;

    case "twitter":
      selectedUrl =
        `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
      break;

    case "telegram":
      selectedUrl =
        `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
      break;

    case "linkedin":
      selectedUrl =
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
      break;

    default:
      console.error(
        "Unsupported sharing platform:",
        type
      );
      return;
  }

  window.open(
    selectedUrl,
    "_blank",
    "noopener,noreferrer"
  );

  setShareOpen(false);
};
  const copyClubShareLink = async () => {
    if (!shareUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
        setShareOpen(false);
      }, 1500);
    } catch (error) {
      console.error("Unable to copy club share link:", error);

      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      setCopied(true);

      setTimeout(() => {
        setCopied(false);
        setShareOpen(false);
      }, 1500);
    }
  };

if (profileLoading) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f5f7fb",
      }}
    >
      <div
        style={{
          width: "42px",
          height: "42px",
          border: "4px solid #dbeafe",
          borderTopColor: "#2563eb",
          borderRadius: "50%",
          animation: "clubLoaderSpin 0.8s linear infinite",
        }}
      />

      <style>{`
        @keyframes clubLoaderSpin {
          from {
            transform: rotate(0deg);
          }

          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}
  if (error || !clubProfile) {
    return (
      <div className="p-5">
        Club not found
        <br />
        <button
          className="btn btn-primary mt-3"
          onClick={() => navigate("/home")}
        >
          Go Home
        </button>
      </div>
    );
  }

  // Only show posts that belong to the currently opened club.
  // This protects the UI from briefly showing stale Redux data
  // from the previously opened club.
  const currentClubPosts = Array.isArray(clubPosts)
    ? clubPosts.filter(
        (post: any) =>
          Number(post.client_id) === Number(clubProfile?.client_id)
      )
    : [];

  // /client/members/:client_id returns a flat members array.
  // Wrap it into one UI group so the existing Members design can stay unchanged.
  const memberList = Array.isArray(membersData?.members)
    ? membersData.members
    : [];

  const memberGroups =
    memberList.length > 0
      ? [
          {
            group_id: "club-members",
            group_name: "Club Members",
            members: memberList,
          },
        ]
      : [];
  const totalMembers = memberGroups.reduce(
    (sum: number, group: any) => sum + (group.members?.length || 0),
    0
  );
  const maleMembers = memberGroups.reduce(
    (sum: number, group: any) =>
      sum +
      (group.members || []).filter(
        (member: any) => String(member.gender || "").toLowerCase() === "male"
      ).length,
    0
  );
  const femaleMembers = memberGroups.reduce(
    (sum: number, group: any) =>
      sum +
      (group.members || []).filter(
        (member: any) => String(member.gender || "").toLowerCase() === "female"
      ).length,
    0
  );

  const eventList = Array.isArray(eventsData?.events)
    ? eventsData.events
    : [];
  const now = new Date();
  const upcomingEvents = eventList.filter(
    (event: any) =>
      event.from_date_time && new Date(event.from_date_time).getTime() >= now.getTime()
  );
  const pastEvents = eventList.filter(
    (event: any) =>
      !event.from_date_time || new Date(event.from_date_time).getTime() < now.getTime()
  );

  const documentList = Array.isArray(documentsData?.documents)
    ? documentsData.documents
    : [];

  const mediaList = Array.isArray(mediaData?.data) ? mediaData.data : [];

  return (
    <Fragment>
      <Header />
      <Leftnav />
      {/* <Rightchat /> */}

      <div
        className="main-content right-chat-active club-home-main"
        style={{
          paddingTop: "78px",
        }}
      >
        <div
          className="middle-sidebar-bottom"
          style={{
            maxWidth: "100%",
            width: "100%",
          }}
        >
          <div
            className="middle-sidebar-left"
            style={{
              width: "100%",
              maxWidth: "100%",
              paddingLeft: "4px",
              paddingRight: "0px",
            }}
          >
            <div
              className="middle-wrap"
              style={{
                maxWidth: "100%",
                width: "100%",
                paddingTop: "8px",
              }}
            >
              <div
                className="row"
                style={{
                  alignItems: "flex-start",
                  marginLeft: "0px",
                  marginRight: "0px",
                  columnGap: "6px",
                }}
              >
                <div className="col-xl-8 col-xxl-8 col-lg-8">
               <div
  className="card border-0 shadow-xss rounded-xxl mb-3"
  style={{
    overflow: "visible",
    position: "relative",
    zIndex: shareOpen ? 30 : 1,
  }}
>
  {/* Banner */}
  <div
    className="club-profile-banner"
    style={{
      height: window.innerWidth <= 768 ? "120px" : "165px",
      backgroundImage: `url(${getImageUrl(clubProfile.banner_image)})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundColor: "#eef2f7",
      borderTopLeftRadius: "20px",
      borderTopRightRadius: "20px",
    }}
  />

  <div
    className="card-body club-profile-body"
    style={{
      padding: window.innerWidth <= 768 ? "14px" : "18px",
    }}
  >
    {/* Top Section */}
    <div
      className="d-flex club-profile-top"
      style={{
        flexDirection: window.innerWidth <= 768 ? "column" : "row",
        alignItems:
          window.innerWidth <= 768 ? "center" : "flex-start",
        textAlign:
          window.innerWidth <= 768 ? "center" : "left",
      }}
    >
      {/* Logo */}
      <img
        src={getImageUrl(clubProfile.logo_image)}
        alt={clubProfile.client_name}
        className="shadow-sm club-profile-logo"
        style={{
          width: window.innerWidth <= 768 ? "78px" : "92px",
          height: window.innerWidth <= 768 ? "78px" : "92px",
          borderRadius: "50%",
          objectFit: "cover",
          border: "5px solid #fff",
          marginTop: window.innerWidth <= 768 ? "-48px" : "-58px",
          background: "#fff",
        }}
      />

      {/* Content */}
      <div
        className={`club-profile-content ${
          window.innerWidth <= 768 ? "mt-2" : "ms-3"
        }`}
        style={{
          flex: 1,
          width: "100%",
        }}
      >
        {/* Club Name */}
        <div
          className="d-flex align-items-center flex-wrap gap-2 club-title-row"
          style={{
            justifyContent:
              window.innerWidth <= 768
                ? "center"
                : "flex-start",
          }}
        >
          <h2
            className="fw-600 text-grey-900 mb-0 club-profile-title"
            style={{
              fontSize:
                window.innerWidth <= 768 ? "24px" : "32px",
              lineHeight: "1.2",
            }}
          >
            {clubProfile?.club_name}
          </h2>

          {Number(clubProfile?.verified_status) === 1 && (
            <VerifiedTick />
          )}
        </div>

        {/* Subtitle + Compact Share */}
        <div
          className="font-xssss text-grey-500 mt-1 d-flex align-items-center flex-wrap"
          style={{
            lineHeight: "20px",
            gap: "7px",
            justifyContent:
              window.innerWidth <= 768
                ? "center"
                : "flex-start",
          }}
        >
          <span>
            {clubProfile.level_name ||
              clubProfile.level?.level_name ||
              "Club"}
          </span>

          {clubProfile.establish_date && (
            <>
              <span>•</span>

              <span>
                Estd.{" "}
                {formatDate(
                  clubProfile.establish_date
                )}
              </span>
            </>
          )}

          <span>•</span>

          <div
            ref={shareRef}
            className="club-share-wrapper"
          >
            <button
              type="button"
              className={`club-share-button ${
                shareOpen ? "active" : ""
              }`}
              onClick={() =>
                setShareOpen((previous) => !previous)
              }
              aria-expanded={shareOpen}
              aria-label="Share club profile"
            >
              <span className="feather-share-2" />
              <span>Share</span>
            </button>

            {shareOpen && (
              <div className="club-share-popup">
                <div className="club-share-heading">
                  <div>
                    <span className="club-share-kicker">
                      SHARE
                    </span>

                    <strong>
                      Share this club
                    </strong>

                    <p>
                      Invite others to explore this club on Bharat Sangam.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="club-share-close"
                    onClick={() => setShareOpen(false)}
                    aria-label="Close share popup"
                  >
                    ×
                  </button>
                </div>

                <div className="club-share-platforms">
                  <button
                    type="button"
                    className="facebook"
                    onClick={() =>
                      handleClubShare("facebook")
                    }
                    aria-label="Share on Facebook"
                  >
                    <span>
                      <SocialIcon icon={FaFacebookF} />
                    </span>
                    Facebook
                  </button>

                  <button
                    type="button"
                    className="twitter"
                    onClick={() =>
                      handleClubShare("twitter")
                    }
                    aria-label="Share on Twitter"
                  >
                    <span>
                      <SocialIcon icon={FaTwitter} />
                    </span>
                    Twitter
                  </button>

                  <button
                    type="button"
                    className="whatsapp"
                    onClick={() =>
                      handleClubShare("whatsapp")
                    }
                    aria-label="Share on WhatsApp"
                  >
                    <span>
                      <SocialIcon icon={FaWhatsapp} />
                    </span>
                    WhatsApp
                  </button>

                  <button
                    type="button"
                    className="linkedin"
                    onClick={() =>
                      handleClubShare("linkedin")
                    }
                    aria-label="Share on LinkedIn"
                  >
                    <span>
                      <SocialIcon icon={FaLinkedinIn} />
                    </span>
                    LinkedIn
                  </button>

                  <button
                    type="button"
                    className="telegram"
                    onClick={() =>
                      handleClubShare("telegram")
                    }
                    aria-label="Share on Telegram"
                  >
                    <span>
                      <SocialIcon icon={FaTelegramPlane} />
                    </span>
                    Telegram
                  </button>
                </div>

                <div className="club-share-link-label">
                  Public club link
                </div>

                <div className="club-copy-link">
                  <span title={shareUrl}>
                    {shareUrl}
                  </span>

                  <button
                    type="button"
                    onClick={copyClubShareLink}
                    className={copied ? "copied" : ""}
                  >
                    {copied ? (
                      <>
                        <span className="feather-check" />
                        Copied
                      </>
                    ) : (
                      <>
                        <span className="feather-copy" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Description */}
    <p
      className="font-xssss text-grey-600 mt-3 mb-3 club-profile-description"
      style={{
        maxWidth: "720px",
        lineHeight: "22px",
        textAlign:
          window.innerWidth <= 768 ? "center" : "left",
      }}
    >
      {clubProfile.short_description ||
        clubProfile.description ||
        "No description added."}
    </p>

    {/* Info Boxes */}
    <div
      className="club-info-grid"
      style={{
        display: "grid",
        gridTemplateColumns:
          window.innerWidth <= 768
            ? "repeat(2,1fr)"
            : "repeat(5,1fr)",
        gap: "10px",
      }}
    >
      <InfoBox
        label="Registered On"
        value={formatDate(clubProfile.establish_date)}
      />

      <InfoBox
        label="Register No"
        value={clubProfile.register_number}
      />

      <InfoBox
        label="Total Members"
        value={clubProfile.member_count || 0}
      />

      <InfoBox
        label="President"
        value={clubProfile.leadership_name}
      />

      <InfoBox
        label="Followers"
        value={clubProfile.follower_count || 0}
      />
    </div>
  </div>
</div>

                  <div className="card border-0 shadow-xss rounded-xxl mb-3">
                    <div className="card-body p-0">
                      <div className="d-flex overflow-auto border-bottom">
                        {[
                          "posts",
                          "about",
                          "members",
                          "events",
                          "media",
                          "documents",
                        ].map((tab) => (
                          <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className="btn border-0 rounded-0 px-4 py-3 text-capitalize font-xssss"
                            style={{
                              color:
                                activeTab === tab ? "#0d6efd" : "#8b8ba7",
                              fontWeight: activeTab === tab ? 700 : 500,
                              borderBottom:
                                activeTab === tab
                                  ? "2px solid #0d6efd"
                                  : "2px solid transparent",
                            }}
                          >
                            {tab}
                          </button>
                        ))}
                      </div>

                      <div className="p-3">
                        {activeTab === "posts" && (
                          <>
                            {postsLoading ? (
                              <div className="club-section-loader">
                                <div className="spinner-border text-primary" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                              </div>
                            ) : currentClubPosts.length > 0 ? (
                              currentClubPosts.map((post: any) => (
                                <ClubPostCard key={post.post_id} post={post} />
                              ))
                            ) : (
                              <p className="text-center text-grey-500 py-4 mb-0">
                                No posts available
                              </p>
                            )}
                          </>
                        )}

                     {activeTab === "about" && (
  <>
    {aboutLoading ? (
      <div className="club-section-loader">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ) : aboutData?.can_view_about ? (
      <div>
        {/* About Section */}
        <div
          className="mb-4"
          style={{
            borderBottom: "1px solid #f1f1f1",
            paddingBottom: "24px",
          }}
        >
          <h3
            className="fw-600 text-grey-900 mb-3"
            style={{
              fontSize: "24px",
            }}
          >
            About {aboutData?.client_name}
          </h3>

          <p
            className="text-grey-700 mb-4"
            style={{
              fontSize: "15px",
              lineHeight: "30px",
              whiteSpace: "pre-line",
            }}
          >
            {aboutData?.description ||
              "No description available"}
          </p>

          {aboutData?.short_description && (
            <div
              style={{
                fontSize: "15px",
                lineHeight: "28px",
                color: "#555",
                fontStyle: "italic",
              }}
            >
              “{aboutData.short_description}”
            </div>
          )}
        </div>

        {/* Information Grid */}
        <div className="row">
          <div className="col-md-6 mb-4">
            <div>
              <h5
                className="fw-600 text-grey-900 mb-3"
                style={{
                  fontSize: "17px",
                }}
              >
                Club Information
              </h5>

              <div className="mb-3">
                <div className="text-grey-500 font-xssss mb-1">
                  Club Name
                </div>

                <div className="fw-600 text-grey-900">
                  {aboutData?.client_name || "-"}
                </div>
              </div>

              <div className="mb-3">
                <div className="text-grey-500 font-xssss mb-1">
                  Register Number
                </div>

                <div className="fw-600 text-grey-900">
                  {aboutData?.register_number || "-"}
                </div>
              </div>

              <div className="mb-3">
                <div className="text-grey-500 font-xssss mb-1">
                  Established Date
                </div>

                <div className="fw-600 text-grey-900">
                  {formatDate(aboutData?.establish_date)}
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 mb-4">
            <div>
              <h5
                className="fw-600 text-grey-900 mb-3"
                style={{
                  fontSize: "17px",
                }}
              >
                Contact Details
              </h5>

              <div className="mb-3">
                <div className="text-grey-500 font-xssss mb-1">
                  Email Address
                </div>

                <div className="fw-600 text-grey-900">
                  {aboutData?.contact_email || "-"}
                </div>
              </div>

              <div className="mb-3">
                <div className="text-grey-500 font-xssss mb-1">
                  Mobile Number
                </div>

                <div className="fw-600 text-grey-900">
                  {aboutData?.mobile_number || "-"}
                </div>
              </div>

              <div className="mb-3">
                <div className="text-grey-500 font-xssss mb-1">
                  Website
                </div>

                <div className="fw-600">
                  {aboutData?.website_link ? (
                    <a
                      href={aboutData.website_link}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        color: "#0d6efd",
                        textDecoration: "none",
                      }}
                    >
                      {aboutData.website_link}
                    </a>
                  ) : (
                    <span className="text-grey-900">-</span>
                  )}
                </div>
              </div>

              <div>
                <div className="text-grey-500 font-xssss mb-1">
                  Address
                </div>

                <div
                  className="fw-600 text-grey-900"
                  style={{
                    lineHeight: "24px",
                  }}
                >
                  {aboutData?.location_address || "-"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <PrivateMessage text={aboutData?.private_message} />
    )}
  </>
)}


{/* ========================= MEMBERS SECTION ========================= */}

{activeTab === "members" && (
  <>
    {membersLoading ? (
      <div className="club-section-loader">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ) : membersData?.can_view_members === 0 ? (
      <PrivateMessage
        text={
          membersData?.private_message ||
          "The member list has been made private by the club."
        }
      />
    ) : memberGroups.length > 0 ? (
      <div>
        <div style={sectionHeaderStyle} className="club-members-section-header">
          <div>
            <div className="d-flex align-items-center gap-2">
              <h3 className="fw-600 text-grey-900 mb-0" style={{ fontSize: "18px" }}>
                Members
              </h3>
              <span
                style={{
                  background: "#eef4ff",
                  color: "#2F6FE4",
                  borderRadius: "999px",
                  padding: "4px 9px",
                  fontSize: "11px",
                  fontWeight: 600,
                }}
              >
                {totalMembers}
              </span>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 club-members-tools">
            <div
              className="d-flex align-items-center club-members-search"
              style={{
                background: "#fff",
                border: "1px solid #eef1f5",
                borderRadius: "14px",
                padding: "9px 10px",
                minWidth: window.innerWidth <= 768 ? "135px" : "210px",
              }}
            >
              <span className="feather-search text-grey-500 me-2" />
              <input
                type="text"
                placeholder="Search members..."
                style={{
                  border: 0,
                  outline: "none",
                  width: "100%",
                  fontSize: "12px",
                  background: "transparent",
                }}
              />
            </div>

            <button
              type="button"
              style={{
                width: "38px",
                height: "38px",
                borderRadius: "13px",
                border: "1px solid #eef1f5",
                background: "#fff",
                color: "#64748b",
              }}
            >
              <span className="feather-sliders" />
            </button>
          </div>
        </div>

        {memberGroups.map((group: any, index: number) => {
          const isOpen =
            expandedGroups[group.group_id] !== undefined
              ? expandedGroups[group.group_id]
              : index === 0;

          const groupColor =
            index % 3 === 0 ? "#2F6FE4" : index % 3 === 1 ? "#5B7FEA" : "#3D6DD8";
          const groupBg =
            index % 3 === 0 ? "#eef4ff" : index % 3 === 1 ? "#f2f6ff" : "#edf4ff";

          return (
            <div key={group.group_id} style={{ ...softCardStyle, marginBottom: "14px", overflow: "hidden" }}>
              <button
                type="button"
                onClick={() =>
                  setExpandedGroups((prev) => ({
                    ...prev,
                    [group.group_id]: !isOpen,
                  }))
                }
                style={{
                  width: "100%",
                  border: 0,
                  background: "#fff",
                  padding: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  textAlign: "left",
                }}
              >
                <div className="d-flex align-items-center">
                  <div
                    style={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "50%",
                      background: groupBg,
                      color: groupColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "20px",
                      marginRight: "12px",
                    }}
                  >
                    👥
                  </div>

                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <h4 className="fw-600 text-grey-900 mb-0" style={{ fontSize: "15px" }}>
                        {group.group_name || "Group"}
                      </h4>
                      <span
                        style={{
                          background: groupBg,
                          color: groupColor,
                          borderRadius: "999px",
                          padding: "3px 8px",
                          fontSize: "10px",
                          fontWeight: 600,
                        }}
                      >
                        {group.members?.length || 0} Members
                      </span>
                    </div>

                    <p className="text-grey-500 mb-0" style={{ fontSize: "11px", marginTop: "3px" }}>
                      {index === 0 ? "Key decision makers and office bearers" : "Active club members"}
                    </p>
                  </div>
                </div>

                <span
                  className={isOpen ? "feather-chevron-up" : "feather-chevron-down"}
                  style={{ color: groupColor, fontSize: "18px" }}
                />
              </button>

              {isOpen && (
                <div>
                  <div
                    className="club-members-table-head"
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        window.innerWidth <= 768
                          ? "1.45fr 1fr 0.7fr 0.8fr 0.65fr"
                          : "2fr 1.2fr 0.8fr 0.9fr 1fr",
                      gap: "8px",
                      background: "#f8fafc",
                      padding: "10px 14px",
                      fontSize: "10px",
                      fontWeight: 600,
                      color: "#64748b",
                    }}
                  >
                    <span>Member</span>
                    <span>Designation</span>
                    <span>Age</span>
                    <span>Gender</span>
                    <span>Contact</span>
                  </div>

                  {(group.members || []).map((member: any) => {
                    const gender = String(member.gender || "").toLowerCase();
                    const isMale = gender === "male";

                    return (
                      <div
                        key={`${group.group_id}-${member.contact_id}-${member.gd_id}`}
                        className="club-member-row"
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            window.innerWidth <= 768
                              ? "1.45fr 1fr 0.7fr 0.8fr 0.65fr"
                              : "2fr 1.2fr 0.8fr 0.9fr 1fr",
                          gap: "8px",
                          alignItems: "center",
                          padding: "12px 14px",
                          borderTop: "1px solid #f1f5f9",
                        }}
                      >
                        <div className="d-flex align-items-center club-member-main-cell" style={{ minWidth: 0 }}>
                          <div style={{ position: "relative", marginRight: "9px" }}>
                            <img
  src={
    member?.photo
      ? getImageUrl(member.photo)
      : "/assets/images/user.png"
  }
  alt={member.contact_name}
  onError={(e) => {
    e.currentTarget.src = "/assets/images/user.png";
  }}
  style={{
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    objectFit: "cover",
    background: "#f1f5f9",
  }}
/>
                            <span
                              style={{
                                position: "absolute",
                                right: "-1px",
                                bottom: "1px",
                                width: "9px",
                                height: "9px",
                                borderRadius: "50%",
                                background: "#22c55e",
                                border: "2px solid #fff",
                              }}
                            />
                          </div>

                          <div style={{ minWidth: 0 }}>
                            <div
                              className="fw-600 text-grey-900"
                              style={{
                                fontSize: "12px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {member.contact_name || "-"}
                            </div>
                            <div className="text-grey-500" style={{ fontSize: "10px" }}>
                              {member.contact_email || ""}
                            </div>
                          </div>
                        </div>

                        <div className="text-grey-700 fw-600 club-member-designation" style={{ fontSize: "11px" }}>
                          {member.designation_name ||
                            member.profession ||
                            "Member"}
                        </div>

                        <div className="text-grey-700 fw-600 club-member-age" style={{ fontSize: "11px" }}>
                          {getAge(member.dob)}
                        </div>

                        <div className="club-member-gender">
                          <span
                            style={{
                              background: isMale ? "#eaf2ff" : "#ffeaf3",
                              color: isMale ? "#2563eb" : "#ec4899",
                              borderRadius: "999px",
                              padding: "4px 7px",
                              fontSize: "10px",
                              fontWeight: 600,
                            }}
                          >
                            {isMale ? "♂ Male" : gender === "female" ? "♀ Female" : "-"}
                          </span>
                        </div>

                        <div className="d-flex align-items-center justify-content-between club-member-contact">
                          <span
                            className="text-grey-700 fw-600"
                            style={{
                              fontSize: "10px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "85px",
                            }}
                          >
                            {member.contact_mobile || "-"}
                          </span>

                          {member.contact_mobile && (
                            <a
                              href={`tel:${member.contact_mobile}`}
                              style={{
                                width: "30px",
                                height: "30px",
                                borderRadius: "50%",
                                border: "1px solid #dce7f7",
                                color: "#2F6FE4",
                                background: "#f5f8ff",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textDecoration: "none",
                              }}
                            >
                              <span className="feather-phone" style={{ fontSize: "13px" }} />
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        <div
          style={{
            ...softCardStyle,
            background: "linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%)",
            borderColor: "#dce7f7",
            padding: "14px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "10px",
            marginTop: "12px",
          }}
        >
          <div className="text-center">
            <div className="text-grey-500" style={{ fontSize: "11px" }}>Total Members</div>
            <div className="fw-600 text-grey-900" style={{ fontSize: "18px" }}>{totalMembers}</div>
          </div>
          <div className="text-center">
            <div style={{ color: "#2563eb", fontSize: "18px" }}>♂</div>
            <div className="fw-600 text-grey-900" style={{ fontSize: "18px" }}>{maleMembers}</div>
            <div className="text-grey-500" style={{ fontSize: "11px" }}>Male</div>
          </div>
          <div className="text-center">
            <div style={{ color: "#ec4899", fontSize: "18px" }}>♀</div>
            <div className="fw-600 text-grey-900" style={{ fontSize: "18px" }}>{femaleMembers}</div>
            <div className="text-grey-500" style={{ fontSize: "11px" }}>Female</div>
          </div>
        </div>
      </div>
    ) : (
      <div className="text-center" style={{ padding: "70px 20px" }}>
        <div style={{ fontSize: "54px", marginBottom: "14px" }}>👥</div>
        <h4 className="fw-600 text-grey-900 mb-2">No Members Available</h4>
        <p className="text-grey-500 mb-0" style={{ fontSize: "14px" }}>
          Members will appear here once added.
        </p>
      </div>
    )}
  </>
)}

{/* ========================= EVENTS SECTION ========================= */}

{activeTab === "events" && (
  <>
    {eventsLoading ? (
      <div className="club-section-loader">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ) : eventsData?.can_view_events === 0 ? (
      <PrivateMessage
        text={
          eventsData?.private_message ||
          "The events section has been made private by the club."
        }
      />
    ) : eventList.length > 0 ? (
      <div>
        <div style={sectionHeaderStyle}>
          <div>
            <h3 className="fw-600 text-grey-900 mb-1" style={{ fontSize: "18px" }}>
              Events
              <span
                className="ms-2"
                style={{
                  background: "#eef4ff",
                  color: "#2F6FE4",
                  borderRadius: "999px",
                  padding: "4px 9px",
                  fontSize: "11px",
                  fontWeight: 600,
                }}
              >
                {upcomingEvents.length} Upcoming
              </span>
            </h3>
          </div>

          <button
            type="button"
            style={{
              border: "1px solid #eef1f5",
              background: "#fff",
              borderRadius: "13px",
              padding: "8px 12px",
              fontSize: "12px",
              fontWeight: 600,
              color: "#475569",
            }}
          >
            <span className="feather-filter me-1" />
            Filter
          </button>
        </div>

        {upcomingEvents.length > 0 && (
          <div className="mb-4">
            <div
              style={{
                background: "#f5f8ff",
                color: "#2F6FE4",
                fontSize: "12px",
                fontWeight: 600,
                padding: "10px 14px",
                borderRadius: "14px 14px 0 0",
              }}
            >
              📅 Upcoming Events
            </div>

            {upcomingEvents.map((event: any) => {
              const dateBox = getDateBox(event.from_date_time);

              return (
                <div
                  key={event.event_id}
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    navigate(
                      `/club/${encodeURIComponent(clubSlug)}/event/${event.event_id}`
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      navigate(
                        `/club/${encodeURIComponent(clubSlug)}/event/${event.event_id}`
                      );
                    }
                  }}
                  style={{
                    ...softCardStyle,
                    borderRadius: "0",
                    marginBottom: "10px",
                    overflow: "hidden",
                    display: "grid",
                    gridTemplateColumns: "74px 1fr 22px",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      background: "#5dc56a",
                      color: "#fff",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "120px",
                    }}
                  >
                    <div className="fw-600" style={{ fontSize: "22px" }}>{dateBox.day}</div>
                    <div className="fw-600" style={{ fontSize: "12px" }}>{dateBox.month}</div>
                    <div style={{ fontSize: "10px" }}>{dateBox.year}</div>
                  </div>

                  <div style={{ padding: "14px" }}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <h4 className="fw-600 text-grey-900 mb-0" style={{ fontSize: "14px" }}>
                        {event.event_title || "Untitled Event"}
                      </h4>
                      <span
                        style={{
                          background: "#eef4ff",
                          color: "#2F6FE4",
                          borderRadius: "999px",
                          padding: "3px 7px",
                          fontSize: "9px",
                          fontWeight: 600,
                        }}
                      >
                        Upcoming
                      </span>
                    </div>

                    <div className="text-grey-600 mb-1" style={{ fontSize: "11px" }}>
                      <span className="feather-map-pin me-1" />
                      {event.location || "Virtual Conference"}
                    </div>

                    <div className="text-grey-600 mb-1" style={{ fontSize: "11px" }}>
                      <span className="feather-calendar me-1" />
                      {formatDate(event.from_date_time)}
                    </div>

                    <div className="text-grey-600" style={{ fontSize: "11px" }}>
                      <span className="feather-clock me-1" />
                      {formatTime(event.from_date_time)} - {formatTime(event.to_date_time)}
                    </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-center">
                    <span className="feather-chevron-right text-grey-400" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {pastEvents.length > 0 && (
          <div>
            <h4 className="fw-600 text-grey-900 mb-3" style={{ fontSize: "16px" }}>
              Past Events
            </h4>

            {pastEvents.map((event: any) => {
              const dateBox = getDateBox(event.from_date_time);

              return (
                <div
                  key={event.event_id}
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    navigate(
                      `/club/${encodeURIComponent(clubSlug)}/event/${event.event_id}`
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      navigate(
                        `/club/${encodeURIComponent(clubSlug)}/event/${event.event_id}`
                      );
                    }
                  }}
                  style={{
                    ...softCardStyle,
                    marginBottom: "12px",
                    overflow: "hidden",
                    display: "grid",
                    gridTemplateColumns: "74px 1fr 22px",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      background: "#475569",
                      color: "#fff",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: "112px",
                      filter: "grayscale(1)",
                    }}
                  >
                    <div className="fw-600" style={{ fontSize: "22px" }}>{dateBox.day}</div>
                    <div className="fw-600" style={{ fontSize: "12px" }}>{dateBox.month}</div>
                    <div style={{ fontSize: "10px" }}>{dateBox.year}</div>
                  </div>

                  <div style={{ padding: "14px", position: "relative" }}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <h4 className="fw-600 text-grey-900 mb-0" style={{ fontSize: "14px" }}>
                        {event.event_title || "Untitled Event"}
                      </h4>
                      <span
                        style={{
                          background: "#f1f5f9",
                          color: "#64748b",
                          borderRadius: "999px",
                          padding: "3px 7px",
                          fontSize: "9px",
                          fontWeight: 600,
                        }}
                      >
                        Past
                      </span>
                    </div>

                    <div className="text-grey-600 mb-1" style={{ fontSize: "11px" }}>
                      <span className="feather-map-pin me-1" />
                      {event.location || "Virtual Conference"}
                    </div>

                    <div className="text-grey-600 mb-1" style={{ fontSize: "11px" }}>
                      <span className="feather-calendar me-1" />
                      {formatDate(event.from_date_time)}
                    </div>

                    <div className="text-grey-600" style={{ fontSize: "11px" }}>
                      <span className="feather-clock me-1" />
                      {formatTime(event.from_date_time)} - {formatTime(event.to_date_time)}
                    </div>

                  
                  </div>

                  <div className="d-flex align-items-center justify-content-center">
                    <span className="feather-chevron-right text-grey-400" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    ) : (
      <div className="text-center" style={{ padding: "70px 20px" }}>
        <div style={{ fontSize: "54px", marginBottom: "14px" }}>📅</div>
        <h4 className="fw-600 text-grey-900 mb-2">No Events Available</h4>
        <p className="text-grey-500 mb-0" style={{ fontSize: "14px" }}>
          Events will appear here once scheduled.
        </p>
      </div>
    )}
  </>
)}

{/* ========================= DOCUMENTS SECTION ========================= */}

{activeTab === "documents" && (
  <>
    {documentsLoading ? (
      <div className="club-section-loader">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ) : documentsData?.can_view_documents === 0 ? (
      <PrivateMessage text={documentsData?.private_message || "Documents section is private."} />
    ) : documentList.length > 0 ? (
      <div>
        <div style={sectionHeaderStyle}>
          <div>
            <h3 className="fw-600 text-grey-900 mb-1" style={{ fontSize: "18px" }}>
              Documents
              <span
                className="ms-2"
                style={{
                  background: "#eef4ff",
                  color: "#2F6FE4",
                  borderRadius: "999px",
                  padding: "4px 9px",
                  fontSize: "11px",
                  fontWeight: 600,
                }}
              >
                {documentsData?.total_documents || documentList.length} Documents
              </span>
            </h3>
          </div>

          <div className="d-flex gap-2">
            <button
              type="button"
              style={{
                width: "38px",
                height: "38px",
                borderRadius: "13px",
                border: "1px solid #eef1f5",
                background: "#fff",
                color: "#64748b",
              }}
            >
              <span className="feather-search" />
            </button>

            <button
              type="button"
              style={{
                border: "1px solid #eef1f5",
                background: "#fff",
                borderRadius: "13px",
                padding: "8px 12px",
                fontSize: "12px",
                fontWeight: 600,
                color: "#475569",
              }}
            >
              <span className="feather-filter me-1" />
              Filter
            </button>
          </div>
        </div>

        {documentList.map((doc: any) => {
          const extension = getFileExtension(doc.file_name || doc.file_url || doc.file_type);
          const fileStyle = getFileStyle(doc.file_type || extension);

          return (
            <div
              key={doc.file_id}
              style={{
                ...softCardStyle,
                marginBottom: "12px",
                padding: "12px",
                display: "grid",
                gridTemplateColumns: "48px 1fr auto",
                gap: "12px",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: "46px",
                  height: "46px",
                  borderRadius: "13px",
                  background: fileStyle.bg,
                  color: fileStyle.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: 600,
                }}
              >
                {fileStyle.icon}
              </div>

              <div style={{ minWidth: 0 }}>
                <h4
                  className="fw-600 text-grey-900 mb-1"
                  style={{
                    fontSize: "13px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {doc.file_name || "Document"}
                </h4>

                <div className="text-grey-500" style={{ fontSize: "10px" }}>
                  {extension} • Uploaded on {formatDate(doc.created_at)}
                </div>
              </div>

              <div className="d-flex gap-2">
                <a
                  href={getFileUrl(doc.file_url)}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    width: "34px",
                    height: "34px",
                    borderRadius: "12px",
                    border: "1px solid #eef1f5",
                    color: "#64748b",
                    background: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textDecoration: "none",
                  }}
                >
                  <span className="feather-eye" />
                </a>

                <a
                  href={getFileUrl(doc.file_url)}
                  download
                  style={{
                    width: "34px",
                    height: "34px",
                    borderRadius: "12px",
                    border: "1px solid #dce7f7",
                    color: "#2F6FE4",
                    background: "#f5f8ff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textDecoration: "none",
                  }}
                >
                  <span className="feather-download" />
                </a>
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <div className="text-center" style={{ padding: "70px 20px" }}>
        <div style={{ fontSize: "54px", marginBottom: "14px" }}>📂</div>
        <h4 className="fw-600 text-grey-900 mb-2">No Documents Available</h4>
        <p className="text-grey-500 mb-0" style={{ fontSize: "14px" }}>
          Uploaded club documents will appear here.
        </p>
      </div>
    )}
  </>
)}

{/* ========================= MEDIA SECTION ========================= */}

{activeTab === "media" && (
  <>
    {mediaLoading ? (
      <div className="club-section-loader">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ) : mediaData?.can_view_media === 0 ? (
      <PrivateMessage
        text={mediaData?.private_message || "Media section is private."}
      />
    ) : mediaList.length > 0 ? (
      <div>
        <div style={sectionHeaderStyle}>
          <div>
            <h3
              className="fw-600 text-grey-900 mb-1"
              style={{ fontSize: "18px" }}
            >
              Media Albums
              <span
                className="ms-2"
                style={{
                  background: "#eef4ff",
                  color: "#2F6FE4",
                  borderRadius: "999px",
                  padding: "4px 9px",
                  fontSize: "11px",
                  fontWeight: 600,
                }}
              >
                {mediaData?.total_albums || mediaData?.total_media || mediaList.length} Albums
              </span>
            </h3>

            <p className="text-grey-500 mb-0" style={{ fontSize: "11px" }}>
              Photos, videos and event memories
            </p>
          </div>

          <div className="d-flex gap-2">
            <button
              type="button"
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "13px",
                border: "1px solid #eef1f5",
                background: "#fff",
                color: "#64748b",
              }}
            >
              <span className="feather-search" />
            </button>

            <button
              type="button"
              style={{
                border: "1px solid #eef1f5",
                background: "#fff",
                borderRadius: "13px",
                padding: "8px 11px",
                fontSize: "11px",
                fontWeight: 600,
                color: "#475569",
              }}
            >
              <span className="feather-sliders me-1" />
              Sort
            </button>
          </div>
        </div>

        {mediaList.map((album: any, index: number) => {
          const albumImages = Array.isArray(album.album_image_urls)
            ? album.album_image_urls
            : Array.isArray(album.album_images)
            ? album.album_images
            : [];

          const coverImage =
            album.cover_image_url ||
            albumImages?.[0] ||
            album.cover_image ||
            "";

          const secondImage = albumImages?.[0] || coverImage;

          const totalImages = Number(
            album.image_count || albumImages.length || 0
          );

          const totalVideos =
            Number(album.video_count || 0) +
            Number(album.video_link_count || 0);

          const extraCount = Math.max(totalImages - 1, 0);

          return (
            <div
              key={album.album_id || index}
              role="button"
              tabIndex={0}
              onClick={() =>
                navigate(
                  `/club/${encodeURIComponent(clubSlug)}/media/${album.album_id}`
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  navigate(
                    `/club/${encodeURIComponent(clubSlug)}/media/${album.album_id}`
                  );
                }
              }}
              style={{
                ...softCardStyle,
                marginBottom: "12px",
                padding: "10px",
                display: "grid",
                gridTemplateColumns:
                  window.innerWidth <= 575 ? "96px 1fr 16px" : "112px 1fr 20px",
                gap: "12px",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              {/* Album Thumbnail */}
              <div
                style={{
                  height: window.innerWidth <= 575 ? "70px" : "78px",
                  borderRadius: "13px",
                  overflow: "hidden",
                  position: "relative",
                  background: "#f1f5f9",
                  display: "grid",
                  gridTemplateColumns: "1fr 40px",
                  gap: "2px",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    background: "#f8fafc",
                  }}
                >
                  {coverImage ? (
                    <img
                      src={getImageUrl(coverImage)}
                      alt={album.album_title || "Album"}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.style.display = "none";
                      }}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  ) : (
                    <MediaPlaceholder />
                  )}
                </div>

                <div style={{ display: "grid", gap: "2px" }}>
                  {secondImage ? (
                    <img
                      src={getImageUrl(secondImage)}
                      alt=""
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.style.display = "none";
                      }}
                      style={{
                        width: "100%",
                        height: "38px",
                        objectFit: "cover",
                        background: "#f8fafc",
                      }}
                    />
                  ) : (
                    <MediaPlaceholder compact />
                  )}

                  <div
                    style={{
                      height: "38px",
                      background: "rgba(15,23,42,0.72)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      fontWeight: 600,
                    }}
                  >
                    +{extraCount}
                  </div>
                </div>
              </div>

              {/* Album Info */}
              <div style={{ minWidth: 0 }}>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h4
                    className="fw-600 text-grey-900 mb-0"
                    style={{
                      fontSize: "13px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {album.album_title || "Club Media"}
                  </h4>

                  {album.visibility && (
                    <span
                      style={{
                        background:
                          album.visibility === "public" ? "#ecfdf3" : "#f5f8ff",
                        color:
                          album.visibility === "public" ? "#16a34a" : "#2F6FE4",
                        borderRadius: "999px",
                        padding: "2px 7px",
                        fontSize: "9px",
                        fontWeight: 600,
                        textTransform: "capitalize",
                        flexShrink: 0,
                      }}
                    >
                      {album.visibility}
                    </span>
                  )}
                </div>

                {album.album_category && (
                  <div
                    className="text-grey-600 mb-1"
                    style={{
                      fontSize: "10px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {album.album_category}
                  </div>
                )}

                <div className="text-grey-500 mb-1" style={{ fontSize: "10px" }}>
                  <span className="feather-image me-1" />
                  {totalImages} Photos

                  <span className="mx-2">•</span>

                  <span className="feather-video me-1" />
                  {totalVideos} Videos
                </div>

                <div
                  className="text-grey-500"
                  style={{
                    fontSize: "10px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <span className="feather-calendar me-1" />
                  {formatDate(album.event_date)}

                  {album.location && (
                    <>
                      <span className="mx-2">•</span>
                      <span className="feather-map-pin me-1" />
                      {album.location}
                    </>
                  )}
                </div>
              </div>

              <span className="feather-chevron-right text-grey-400" />
            </div>
          );
        })}
      </div>
    ) : (
      <div className="text-center" style={{ padding: "70px 20px" }}>
        <div style={{ fontSize: "54px", marginBottom: "14px" }}>🖼️</div>
        <h4 className="fw-600 text-grey-900 mb-2">No Media Available</h4>
        <p className="text-grey-500 mb-0" style={{ fontSize: "14px" }}>
          Photos and videos will appear here once uploaded.
        </p>
      </div>
    )}
  </>
)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-3 col-xxl-3 col-lg-3">
                  <div className="card border-0 shadow-xss rounded-xxl p-3 mb-3">
                    <h4 className="fw-600 text-grey-900 mb-2 font-xsss">
                      About Club
                    </h4>

                    <p
                      className="font-xssss text-grey-500 mb-3"
                      style={{
                        lineHeight: "22px",
                        display: "-webkit-box",
                        WebkitLineClamp: 8,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {clubProfile.description ||
                        clubProfile.short_description ||
                        "-"}
                    </p>

                    <div className="font-xssss text-grey-600">
                      <div className="d-flex align-items-start mb-2">
                        <span className="me-2">✉️</span>
                        <div>
                          <div className="fw-600 text-grey-900">Email</div>
                          {clubProfile.contact_email ||
                            clubProfile.client_email ||
                            "-"}
                        </div>
                      </div>

                      <div className="d-flex align-items-start mb-2">
                        <span className="me-2">📞</span>
                        <div>
                          <div className="fw-600 text-grey-900">Phone</div>
                          {clubProfile.mobile_number ||
                            clubProfile.phone_number ||
                            "-"}
                        </div>
                      </div>

                      <div className="d-flex align-items-start">
                        <span className="me-2">🌐</span>
                        <div>
                          <div className="fw-600 text-grey-900">Website</div>
                          {clubProfile.website_link || "-"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card border-0 shadow-xss rounded-xxl p-3 mb-3">
                    <h4 className="fw-600 text-grey-900 mb-2 font-xsss">
                      Our Location
                    </h4>

                    {clubProfile.latitude && clubProfile.longitude ? (
                      <>
                        <iframe
                          title="club-location"
                          width="100%"
                          height="170"
                          loading="lazy"
                          style={{
                            border: 0,
                            borderRadius: "14px",
                          }}
                          src={`https://maps.google.com/maps?q=${clubProfile.latitude},${clubProfile.longitude}&z=15&output=embed`}
                        />

                        <p className="font-xssss text-grey-500 mt-2 mb-2">
                          {clubProfile.location_address || "-"}
                        </p>

                        <a
                          href={`https://www.google.com/maps?q=${clubProfile.latitude},${clubProfile.longitude}`}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-light bg-greylight rounded-pill w-100 font-xssss"
                        >
                          Open in Google Maps
                        </a>
                      </>
                    ) : (
                      <p className="font-xssss text-grey-500 mb-0">
                        Location not added
                      </p>
                    )}
                  </div>

                  <div className="card border-0 shadow-xss rounded-xxl p-3 mb-3">
                    <h4 className="fw-600 text-grey-900 mb-2 font-xsss">
                      Highlights
                    </h4>

                    {[
                      "Leadership Training",
                      "Community Service",
                      "Youth Empowerment",
                      "Networking Opportunities",
                    ].map((item) => (
                      <p key={item} className="font-xssss text-grey-500 mb-2">
                        • {item}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      <style>{`
        .club-home-main {
          background: #f5f7fb;
          min-height: 100vh;
          padding-left: 300px !important;
          padding-right: 30px !important;
          overflow-x: hidden;
        }

        .club-home-main .middle-sidebar-bottom {
          width: 100% !important;
          max-width: 100% !important;
        }

        .club-home-main .middle-sidebar-left {
          width: 100% !important;
          max-width: 100% !important;
        }

        .club-home-main .middle-wrap {
          width: 100% !important;
          max-width: 1180px !important;
          margin: 0 auto !important;
        }

        .club-home-main .card {
          border: 1px solid #e9eef7 !important;
          border-radius: 20px !important;
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.035) !important;
        }

        .club-home-main .text-grey-500,
        .club-home-main .text-grey-600,
        .club-home-main .text-grey-700 {
          font-weight: 500 !important;
        }

        .club-home-main h2,
        .club-home-main h3,
        .club-home-main h4,
        .club-home-main h5,
        .club-home-main .text-grey-900 {
          font-weight: 600 !important;
        }

        .club-home-main button,
        .club-home-main .btn,
        .club-home-main .badge {
          font-weight: 500 !important;
        }

        .club-home-main .font-xssss {
          font-weight: 500;
        }

        .club-home-main .club-soft-label,
        .club-home-main .text-grey-500.font-xssss {
          font-weight: 600 !important;
          letter-spacing: 0.2px;
        }

        .club-home-main input::placeholder {
          color: #94a3b8;
          font-weight: 500;
        }

        .club-share-wrapper {
          position: relative;
          display: inline-flex;
          align-items: center;
          width: auto;
          margin: 0;
        }

        .club-share-button {
          min-height: auto;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          border: 0;
          border-radius: 0;
          background: transparent;
          color: #2563eb;
          font-size: 11px;
          line-height: 20px;
          cursor: pointer;
          box-shadow: none;
          transition: color 0.2s ease;
        }

        .club-share-button:hover,
        .club-share-button.active {
          border: 0;
          background: transparent;
          color: #1d4ed8;
          transform: none;
          box-shadow: none;
        }

        .club-share-button .feather-share-2 {
          font-size: 12px;
        }

        .club-share-popup {
          position: absolute;
          top: 28px;
          left: -8px;
          z-index: 9999;
          width: 360px;
          padding: 18px;
          border: 1px solid #e2e9f3;
          border-radius: 20px;
          background: rgba(255, 255, 255, 0.98);
          box-shadow:
            0 26px 70px rgba(15, 23, 42, 0.18),
            0 9px 24px rgba(37, 99, 235, 0.07);
          backdrop-filter: blur(16px);
          animation: clubSharePopupIn 0.2s ease;
        }

        @keyframes clubSharePopupIn {
          from {
            opacity: 0;
            transform: translateY(8px) scale(0.98);
          }

          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .club-share-popup::before {
          content: "";
          position: absolute;
          top: -8px;
          left: 14px;
          width: 16px;
          height: 16px;
          background: #ffffff;
          border-left: 1px solid #e2e9f3;
          border-top: 1px solid #e2e9f3;
          transform: rotate(45deg);
        }

        .club-share-heading {
          position: relative;
          z-index: 1;
          margin-bottom: 16px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }

        .club-share-heading > div {
          min-width: 0;
        }

        .club-share-kicker {
          display: block;
          margin-bottom: 4px;
          color: #2563eb;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.15em;
        }

        .club-share-heading strong {
          display: block;
          color: #0f172a;
          font-size: 16px;
          line-height: 1.3;
        }

        .club-share-heading p {
          margin: 4px 0 0;
          color: #7a889d;
          font-size: 11px;
          line-height: 1.5;
        }

        .club-share-close {
          width: 30px;
          height: 30px;
          flex: 0 0 auto;
          border: 0;
          border-radius: 10px;
          background: #f1f5f9;
          color: #64748b;
          cursor: pointer;
          font-size: 20px;
          line-height: 1;
          transition: all 0.2s ease;
        }

        .club-share-close:hover {
          color: #0f172a;
          background: #e8eef6;
        }

        .club-share-platforms {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 8px;
          margin-bottom: 16px;
        }

        .club-share-platforms button {
          min-width: 0;
          padding: 9px 2px 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 7px;
          border: 1px solid #e7edf5;
          border-radius: 13px;
          background: #ffffff;
          color: #64748b;
          font-size: 9px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .club-share-platforms button:hover {
          color: #0f172a;
          background: #f8fbff;
          border-color: #c8d8ee;
          transform: translateY(-2px);
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.07);
        }

        .club-share-platforms button > span {
          width: 37px;
          height: 37px;
          display: grid;
          place-items: center;
          border-radius: 12px;
          color: #ffffff;
          font-size: 16px;
        }

        .club-share-platforms .facebook > span {
          background: #1877f2;
        }

        .club-share-platforms .twitter > span {
          background: #1da1f2;
        }

        .club-share-platforms .whatsapp > span {
          background: #25d366;
        }

        .club-share-platforms .linkedin > span {
          background: #0077b5;
        }

        .club-share-platforms .telegram > span {
          background: #229ed9;
        }

        .club-share-link-label {
          margin-bottom: 7px;
          color: #526174;
          font-size: 10px;
          font-weight: 600;
        }

        .club-copy-link {
          min-height: 43px;
          display: flex;
          align-items: center;
          overflow: hidden;
          border: 1px solid #dfe7f1;
          border-radius: 12px;
          background: #f8fafc;
        }

        .club-copy-link > span {
          min-width: 0;
          flex: 1;
          padding: 0 11px;
          overflow: hidden;
          color: #64748b;
          font-size: 11px;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .club-copy-link button {
          min-width: 82px;
          align-self: stretch;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          border: 0;
          border-left: 1px solid #dfe7f1;
          background: #ffffff;
          color: #2563eb;
          font-size: 11px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .club-copy-link button:hover {
          background: #eff6ff;
        }

        .club-copy-link button.copied {
          color: #059669;
          background: #ecfdf5;
        }

        .club-section-loader {
          min-height: 180px;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .club-section-loader .spinner-border {
          width: 34px;
          height: 34px;
          border-width: 3px;
          color: #2F6FE4 !important;
        }

        @media (min-width: 1600px) {
          .club-home-main {
            padding-left: 300px !important;
            padding-right: 40px !important;
          }

          .club-home-main .middle-wrap {
            max-width: 1240px !important;
          }
        }

        @media (max-width: 1500px) {
          .club-home-main {
            padding-left: 300px !important;
            padding-right: 30px !important;
          }

          .club-home-main .middle-wrap {
            max-width: 1120px !important;
          }
        }

        @media (max-width: 1366px) {
          .club-home-main {
            padding-left: 295px !important;
            padding-right: 24px !important;
          }

          .club-home-main .middle-wrap {
            max-width: 100% !important;
            margin: 0 !important;
          }
        }

        @media (max-width: 1200px) {
          .club-home-main {
            padding-left: 24px !important;
            padding-right: 18px !important;
            padding-top: 78px !important;
          }
        }

        @media (max-width: 991px) {
          .club-home-main {
            padding-left: 16px !important;
            padding-right: 16px !important;
            padding-top: 72px !important;
          }
        }

        .club-media-placeholder {
          width: 100%;
          height: 100%;
          min-height: 70px;
          background: #f1f5f9;
          color: #94a3b8;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
        }

        .club-media-placeholder.compact {
          min-height: 38px;
          height: 38px;
          font-size: 15px;
        }

        @media (max-width: 768px) {
          .club-members-section-header {
            align-items: flex-start !important;
            flex-direction: column !important;
            gap: 10px !important;
          }

          .club-members-tools {
            width: 100%;
          }

          .club-members-search {
            flex: 1;
            min-width: 0 !important;
            width: auto !important;
          }
        }

        @media (max-width: 575px) {
          .club-members-table-head {
            display: none !important;
          }

          .club-member-row {
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 10px !important;
            padding: 14px !important;
            align-items: stretch !important;
            border-top: 1px solid #edf1f6 !important;
            background: #ffffff;
          }

          .club-member-main-cell {
            grid-column: 1 / -1;
            padding-bottom: 10px;
            border-bottom: 1px solid #edf1f6;
          }

          .club-member-main-cell img {
            width: 44px !important;
            height: 44px !important;
            min-width: 44px;
          }

          .club-member-main-cell .fw-600 {
            font-size: 13px !important;
          }

          .club-member-main-cell .text-grey-500 {
            max-width: 230px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 10px !important;
          }

          .club-member-designation,
          .club-member-age,
          .club-member-gender,
          .club-member-contact {
            min-width: 0;
            min-height: 58px;
            padding: 9px 10px;
            border: 1px solid #edf1f6;
            border-radius: 10px;
            background: #f8fafc;
            display: flex !important;
            flex-direction: column;
            align-items: flex-start !important;
            justify-content: center !important;
            gap: 4px;
          }

          .club-member-designation::before,
          .club-member-age::before,
          .club-member-gender::before,
          .club-member-contact::before {
            display: block;
            color: #94a3b8;
            font-size: 8px;
            font-weight: 700;
            line-height: 1;
            letter-spacing: .25px;
            text-transform: uppercase;
          }

          .club-member-designation::before { content: "Designation"; }
          .club-member-age::before { content: "Age"; }
          .club-member-gender::before { content: "Gender"; }
          .club-member-contact::before { content: "Contact"; }

          .club-member-designation,
          .club-member-age {
            font-size: 11px !important;
          }

          .club-member-gender span {
            width: fit-content;
            white-space: nowrap;
          }

          .club-member-contact {
            position: relative;
            padding-right: 42px;
          }

          .club-member-contact > span {
            max-width: 100% !important;
            font-size: 11px !important;
          }

          .club-member-contact > a {
            position: absolute;
            right: 8px;
            bottom: 8px;
            width: 30px !important;
            height: 30px !important;
          }
        }

        @media (max-width: 575px) {
          .club-share-wrapper {
            width: auto;
            display: inline-flex;
            justify-content: initial;
          }

          .club-share-popup {
            position: fixed;
            top: auto;
            left: 12px;
            right: 12px;
            bottom: 16px;
            width: auto;
            max-width: none;
            padding: 18px 16px;
            border-radius: 22px;
          }

          .club-share-popup::before {
            display: none;
          }

          .club-share-platforms {
            gap: 6px;
          }

          .club-share-platforms button {
            padding: 8px 1px 7px;
            font-size: 8px;
          }

          .club-share-platforms button > span {
            width: 35px;
            height: 35px;
            border-radius: 11px;
          }

          .club-profile-banner {
            height: 120px !important;
          }

          .club-profile-body {
            padding: 14px !important;
          }

          .club-profile-top {
            flex-direction: column !important;
            align-items: center !important;
            text-align: center !important;
          }

          .club-profile-logo {
            width: 78px !important;
            height: 78px !important;
            margin-top: -48px !important;
          }

          .club-profile-content {
            width: 100% !important;
            margin-left: 0 !important;
            margin-top: 8px !important;
          }

          .club-title-row {
            justify-content: center !important;
          }

          .club-profile-title {
            font-size: 22px !important;
          }

          .club-profile-description {
            text-align: center !important;
          }

          .club-info-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 8px !important;
          }

          .club-info-grid > div {
            min-width: 0 !important;
          }

          .club-home-main {
            padding-left: 12px !important;
            padding-right: 12px !important;

            /* Small breathing space below the fixed mobile header */
            padding-top: 16px !important;
          }

          .club-home-main .middle-sidebar-bottom,
          .club-home-main .middle-sidebar-left,
          .club-home-main .middle-wrap {
            width: 100% !important;
            max-width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }

          .club-home-main .middle-wrap {
            padding-top: 0 !important;
          }

          .club-home-main .row {
            margin-left: 0 !important;
            margin-right: 0 !important;
            row-gap: 12px;
          }

          .club-home-main .row > [class*="col-"] {
            width: 100%;
            max-width: 100%;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }

          .club-home-main .card {
            margin-bottom: 12px !important;
          }
        }
      `}</style>

      <Popupchat />
      <Appfooter />
    </Fragment>
  );
};

export default ClubHomePage;